import React, { useEffect, useState } from "react";
import "./AdaptiveEvalutionGraph.css";
import DifficultyChart from "./DifficultyChart";
import { NavLink, useLocation } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  Divider,
  IconButton,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";

function AdaptiveEvalutionGraph({
  studentMarksDetails,
  setOpenGraphPopup,
  reEvaluate,
  studentId,
  setInProgress,
  studentName,
  setPublishResult,
  chartData,
  studentResultData,
  isHideSummaryBtn,
  evaluationData,
  assessmentData,
  studentDetails,
  assessmentisAdaptive,
}) {
  const { state } = useLocation();

  const cumulativeValues = [];
  let runningTotal = 0;
  studentResultData?.results?.forEach((res) => {
    const obtained = res?.obtained_marks ?? 0;
    runningTotal += obtained;
    cumulativeValues.push(runningTotal);
  });

  const checkScoreStatus = (res) => {
    const q = res?.question_response?.question;
    if (!q) return "wrong";
    const obtained = res?.obtained_marks ?? 0;
    const correctScore = q?.correct_score ?? 0;
    if (obtained === 0) return "wrong";
    if (obtained === correctScore) return "correct";
    if (obtained > 0 && obtained < correctScore) return "partial";
    return "wrong";
  };

  const chartDatas = {
    labels: chartData?.map((item) => item.questionNumber),
    values: assessmentisAdaptive
      ? studentResultData?.difficulty_history?.map((item) => item.ability) || []
      : cumulativeValues,
    isCorrect: assessmentisAdaptive
      ? studentResultData?.difficulty_history?.map((item) =>
          item.isCorrect ? "correct" : "wrong",
        )
      : studentResultData?.results?.map((res) => checkScoreStatus(res)),
    data: studentResultData?.results?.map((item) => item.question_response),
    isDescriptive: studentResultData?.results?.map(
      (item) => item?.question_response?.question?.type?.type === "descriptive",
    ),
    teacherComments: studentResultData?.results?.map((item) => item?.comment),
    obtained_marks: studentResultData?.results?.map(
      (item) => item?.obtained_marks,
    ),
  };

  const [scores, setScores] = useState({
    VE: { correct: 0, wrong: 0, partial: 0 },
    E: { correct: 0, wrong: 0, partial: 0 },
    M: { correct: 0, wrong: 0, partial: 0 },
    H: { correct: 0, wrong: 0, partial: 0 },
    VH: { correct: 0, wrong: 0, partial: 0 },
    None: { correct: 0, wrong: 0, partial: 0 },
  });

  useEffect(() => {
    function populateCorrectWrongCount() {
      let updatedScores = {
        VE: { correct: 0, wrong: 0, partial: 0 },
        E: { correct: 0, wrong: 0, partial: 0 },
        M: { correct: 0, wrong: 0, partial: 0 },
        H: { correct: 0, wrong: 0, partial: 0 },
        VH: { correct: 0, wrong: 0, partial: 0 },
        None: { correct: 0, wrong: 0, partial: 0 },
      };

      if (assessmentisAdaptive) {
        studentResultData?.difficulty_history?.forEach((item) => {
          const difficulty = item.difficulty;
          const isCorrect = item.isCorrect;
          switch (difficulty) {
            case 1:
              updatedScores.VE.correct += isCorrect ? 1 : 0;
              updatedScores.VE.wrong += !isCorrect ? 1 : 0;
              break;
            case 2:
              updatedScores.E.correct += isCorrect ? 1 : 0;
              updatedScores.E.wrong += !isCorrect ? 1 : 0;
              break;
            case 3:
              updatedScores.M.correct += isCorrect ? 1 : 0;
              updatedScores.M.wrong += !isCorrect ? 1 : 0;
              break;
            case 4:
              updatedScores.H.correct += isCorrect ? 1 : 0;
              updatedScores.H.wrong += !isCorrect ? 1 : 0;
              break;
            case 5:
              updatedScores.VH.correct += isCorrect ? 1 : 0;
              updatedScores.VH.wrong += !isCorrect ? 1 : 0;
              break;
            case 6:
              updatedScores.None.correct += isCorrect ? 1 : 0;
              updatedScores.None.wrong += !isCorrect ? 1 : 0;
              break;
            default:
              break;
          }
        });
      } else {
        studentResultData?.results?.forEach((res) => {
          const q = res?.question_response?.question;
          if (!q || !q.difficulty_level) return;
          const diffName = q.difficulty_level.display_name;
          let key;
          switch (diffName) {
            case "Very Easy":
              key = "VE";
              break;
            case "Easy":
              key = "E";
              break;
            case "Medium":
              key = "M";
              break;
            case "Hard":
            case "Difficult":
              key = "H";
              break;
            case "Very Hard":
            case "Very Difficult":
              key = "VH";
              break;
            case "None":
              key = "None";
              break;
            default:
              key = null;
          }
          if (!key) return;

          const obtained = res?.obtained_marks ?? 0;
          const correctScore = q?.correct_score ?? 0;
          if (obtained === 0) updatedScores[key].wrong += 1;
          else if (obtained === correctScore) updatedScores[key].correct += 1;
          else if (obtained > 0 && obtained < correctScore)
            updatedScores[key].partial += 1;
        });
      }
      setScores(updatedScores);
    }
    populateCorrectWrongCount();
  }, [studentResultData, assessmentisAdaptive]);

  const data = [
    { level: "Very Easy", ...scores.VE },
    { level: "Easy", ...scores.E },
    { level: "Medium", ...scores.M },
    { level: "Hard", ...scores.H },
    { level: "Very Hard", ...scores.VH },
    { level: "Not Set", ...scores.None },
  ];

  const scoreValue = assessmentisAdaptive
    ? studentResultData?.ability?.toFixed(2)
    : `${studentResultData?.obtained_marks ?? 0}/${studentResultData?.assessment?.total_marks ?? 0}`;

  const totalQuestions =
    studentMarksDetails?.attempted_questions?.toString().padStart(2, "0") ||
    studentResultData?.results?.length?.toString().padStart(2, "0");

  const correctCount = assessmentisAdaptive
    ? Number(studentResultData?.correct_count)?.toString().padStart(2, "0")
    : chartDatas.isCorrect?.filter((i) => i === "correct").length;

  const partialCount = assessmentisAdaptive
    ? null
    : chartDatas.isCorrect?.filter((i) => i === "partial").length;

  const wrongCount = assessmentisAdaptive
    ? studentResultData?.incorrect_count
    : chartDatas.isCorrect?.filter((i) => i === "wrong").length;

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        bgcolor: "rgba(15,23,42,0.5)",
        backdropFilter: "blur(3px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1500,
        p: 2,
      }}
      onClick={() => setOpenGraphPopup(false)}
    >
      <Paper
        elevation={0}
        onClick={(e) => e.stopPropagation()}
        sx={{
          width: "min(1050px, 96vw)",
          maxHeight: "96vh",
          borderRadius: 3,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 30px 70px rgba(0,0,0,0.35)",
        }}
      >
        {/* ───────── HEADER ───────── */}
        <Box
          sx={{
            px: 2.5,
            py: 1.25,
            background: "linear-gradient(135deg,#f8fafc,#eef2ff)",
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography fontWeight={700} fontSize={14} color="#111827" noWrap>
              {studentName}
            </Typography>
            <Typography fontSize={11} color="text.secondary" noWrap>
              {assessmentData?.standard?.name}-{assessmentData?.section?.name} ·{" "}
              {assessmentData?.name}
            </Typography>
          </Box>

          {!assessmentisAdaptive && (
            <Stack direction="row" spacing={1.75} alignItems="center">
              {[
                { color: "#00b050", label: "Correct" },
                { color: "#ed7d31", label: "Partial" },
                { color: "#c00000", label: "Incorrect" },
              ].map((l) => (
                <Stack
                  key={l.label}
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: l.color,
                    }}
                  />
                  <Typography fontSize={11} color="#374151">
                    {l.label}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          )}

          <IconButton
            onClick={() => setOpenGraphPopup(false)}
            size="small"
            aria-label="close"
            sx={{
              color: "#9CA3AF",
              "&:hover": { color: "#374151", bgcolor: "#F3F4F6" },
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        {/* ───────── BODY (single view, no scroll) ───────── */}

        {/* Chart — fixed small height */}
        <Box sx={{ px: 2.5, py: 1.5 }}>
          {/* Chart — FULL WIDTH, compact height */}
          <Box sx={{ width: "100%", height: 240, mb: 1.5 }}>
            <DifficultyChart
              data={chartDatas}
              assessmentIsAdaptive={assessmentisAdaptive}
              studentResultData={studentResultData}
            />
          </Box>

          <Divider sx={{ borderColor: "#f3f4f6", mb: 1.5 }} />

          {/* Stats + Table side-by-side, compact */}
          <Box
            sx={{
              display: "flex",
              gap: 2.5,
              alignItems: "flex-start",
            }}
          >
            {/* Left — summary stats */}
            <Box sx={{ flex: "1 1 200px", minWidth: 200 }}>
              {[
                { label: "Score", value: scoreValue, color: "#1d4ed8" },
                {
                  label: "Total Questions",
                  value: totalQuestions,
                  color: "#111827",
                },
                { label: "Correct", value: correctCount, color: "#15803d" },
                ...(!assessmentisAdaptive
                  ? [
                      {
                        label: "Partial",
                        value: partialCount,
                        color: "#ea580c",
                      },
                    ]
                  : []),
                { label: "Wrong", value: wrongCount, color: "#b91c1c" },
              ].map((row) => (
                <Stack
                  key={row.label}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ py: 0.6, borderBottom: "1px dashed #f3f4f6" }}
                >
                  <Typography fontSize={12} color="text.secondary">
                    {row.label}
                  </Typography>
                  <Typography fontSize={13} fontWeight={700} color={row.color}>
                    {row.value ?? 0}
                  </Typography>
                </Stack>
              ))}
            </Box>

            {/* Right — difficulty table, compact */}
            <Box sx={{ flex: "1 1 320px", minWidth: 320 }}>
              <Typography
                fontSize={11}
                fontWeight={700}
                color="#6b7280"
                sx={{
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  mb: 0.75,
                }}
              >
                Difficulty Breakdown
              </Typography>

              <Box
                component="table"
                sx={{
                  width: "100%",
                  borderCollapse: "separate",
                  borderSpacing: 0,
                  fontSize: 12,
                  "& th": {
                    fontWeight: 600,
                    fontSize: 10,
                    textTransform: "uppercase",
                    letterSpacing: 0.3,
                    color: "#6b7280",
                    backgroundColor: "#f9fafb",
                    padding: "7px 10px",
                    textAlign: "center",
                    borderBottom: "1px solid #e5e7eb",
                  },
                  "& td": {
                    padding: "6px 10px",
                    textAlign: "center",
                    borderBottom: "1px solid #f3f4f6",
                  },
                }}
              >
                <thead>
                  <tr>
                    <th style={{ textAlign: "left" }}>Level</th>
                    <th style={{ color: "#15803d" }}>Correct</th>
                    {!assessmentisAdaptive && (
                      <th style={{ color: "#ea580c" }}>Partial</th>
                    )}
                    <th style={{ color: "#b91c1c" }}>Incorrect</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, i) => (
                    <tr key={i}>
                      <td
                        style={{
                          textAlign: "left",
                          color: "#374151",
                          fontWeight: 500,
                        }}
                      >
                        {row.level}
                      </td>
                      <td style={{ color: "#15803d", fontWeight: 600 }}>
                        {row.correct}
                      </td>
                      {!assessmentisAdaptive && (
                        <td style={{ color: "#ea580c", fontWeight: 600 }}>
                          {row.partial}
                        </td>
                      )}
                      <td style={{ color: "#b91c1c", fontWeight: 600 }}>
                        {row.wrong}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* ───────── FOOTER ───────── */}
        {!isHideSummaryBtn && (
          <Box
            sx={{
              px: 2.5,
              py: 1.25,
              borderTop: "1px solid #e5e7eb",
              background: "#fafafa",
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
            }}
          >
            {!reEvaluate ? (
              <NavLink
                to="/reviewallquestions"
                state={{
                  studentId: studentId,
                  assessmentId: state.assessmentId,
                  assessmentData: assessmentData,
                }}
                style={{ textDecoration: "none" }}
              >
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<EmojiEventsRoundedIcon sx={{ fontSize: 15 }} />}
                  sx={{
                    background: "linear-gradient(135deg,#f59e0b,#d97706)",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 12,
                    borderRadius: 2,
                    px: 2,
                    py: 0.7,
                    boxShadow: "0 3px 10px rgba(245,158,11,.25)",
                    "&:hover": {
                      background: "linear-gradient(135deg,#d97706,#b45309)",
                    },
                  }}
                >
                  View Evaluation
                </Button>
              </NavLink>
            ) : (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setPublishResult((prev) => !prev);
                    setInProgress(false);
                  }}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 12,
                    borderRadius: 2,
                    px: 2,
                    py: 0.7,
                    borderColor: "#e5e7eb",
                    color: "#374151",
                    "&:hover": { borderColor: "#d1d5db", bgcolor: "#f9fafb" },
                  }}
                >
                  Re-evaluation
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => window.history.back()}
                  sx={{
                    background: "linear-gradient(135deg,#2563eb,#1d4ed8)",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 12,
                    borderRadius: 2,
                    px: 2,
                    py: 0.7,
                    boxShadow: "0 3px 10px rgba(37,99,235,.25)",
                    "&:hover": {
                      background: "linear-gradient(135deg,#1d4ed8,#1e40af)",
                    },
                  }}
                >
                  Confirm
                </Button>
              </>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
}

export default AdaptiveEvalutionGraph;
